'use client'

import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Link } from '@/components/site/link'
import { useTranslations } from 'next-intl'
import { ArrowUpRight } from 'lucide-react'
import { cn } from 'cn'
import type { ToneMap } from './service-dither-field'
import { SiteSection } from '@/components/site/site-section'
import type { Locale } from '@/types/site'

// Three.js stays out of the eager bundle — React.lazy (unlike next/dynamic)
// doesn't preload the chunk, so it only downloads and evaluates once the
// services section actually enters the viewport.
const ServiceDitherField = lazy(() =>
  import('./service-dither-field').then((m) => ({ default: m.ServiceDitherField })),
)

type IconNode = [tag: string, attrs: Record<string, string>][]

// Lucide icon node data (from lucide-react dist icon definitions).
const ICON_NODES: Record<'launch' | 'city' | 'network' | 'globe', IconNode> = {
  launch: [
    ['path', { d: 'M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5' }],
    [
      'path',
      {
        d: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09',
      },
    ],
    [
      'path',
      {
        d: 'M9 12a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.4 22.4 0 0 1-4 2z',
      },
    ],
    ['path', { d: 'M9 12H4s.55-3.03 2-4c1.62-1.08 5 .05 5 .05' }],
  ],
  city: [
    ['path', { d: 'M10 12h4' }],
    ['path', { d: 'M10 8h4' }],
    ['path', { d: 'M14 21v-3a2 2 0 0 0-4 0v3' }],
    ['path', { d: 'M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2' }],
    ['path', { d: 'M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16' }],
  ],
  network: [
    [
      'path',
      {
        d: 'M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z',
      },
    ],
    ['path', { d: 'M9 13a4.5 4.5 0 0 0 3-4' }],
    ['path', { d: 'M6.003 5.125A3 3 0 0 0 6.401 6.5' }],
    ['path', { d: 'M3.477 10.896a4 4 0 0 1 .585-.396' }],
    ['path', { d: 'M6 18a4 4 0 0 1-1.967-.516' }],
    ['path', { d: 'M12 13h4' }],
    ['path', { d: 'M12 18h6a2 2 0 0 1 2 2v1' }],
    ['path', { d: 'M12 8h8' }],
    ['path', { d: 'M16 8V5a2 2 0 0 1 2-2' }],
    ['circle', { cx: '16', cy: '13', r: '.5' }],
    ['circle', { cx: '18', cy: '3', r: '.5' }],
    ['circle', { cx: '20', cy: '21', r: '.5' }],
    ['circle', { cx: '20', cy: '8', r: '.5' }],
  ],
  globe: [
    ['path', { d: 'M21.54 15H17a2 2 0 0 0-2 2v4.54' }],
    [
      'path',
      {
        d: 'M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17',
      },
    ],
    ['path', { d: 'M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05' }],
    ['circle', { cx: '12', cy: '12', r: '10' }],
  ],
}

// 128 cells across keeps the logo and icon strokes several dots wide, so the
// shapes read filled rather than outlined.
const GLYPH_GRID = 128

// Rasterize any image source into a tone map (alpha coverage → hex rows).
function rasterizeSrc(src: string): Promise<ToneMap> {
  const img = new Image()
  img.src = src
  return new Promise((resolve, reject) => {
    img.onload = () => {
      const px = GLYPH_GRID * 4
      const canvas = document.createElement('canvas')
      canvas.width = canvas.height = px
      const ctx = canvas.getContext('2d')
      if (!ctx) return reject(new Error('no 2d context'))
      ctx.drawImage(img, 0, 0, px, px)
      const data = ctx.getImageData(0, 0, px, px).data
      const rows: string[] = []
      const s = px / GLYPH_GRID
      for (let y = 0; y < GLYPH_GRID; y++) {
        let row = ''
        for (let x = 0; x < GLYPH_GRID; x++) {
          let cov = 0
          for (let sy = 0; sy < s; sy++)
            for (let sx = 0; sx < s; sx++)
              cov += data[((y * s + sy) * px + x * s + sx) * 4 + 3] / 255
          // Near-binary threshold → uniform dot density along the strokes.
          row += cov / (s * s) > 0.2 ? 'f' : '0'
        }
        rows.push(row)
      }
      resolve({ width: GLYPH_GRID, height: GLYPH_GRID, rows })
    }
    img.onerror = reject
  })
}

// Rasterize the lucide icon strokes into a tone map so the icon is drawn in sand.
function rasterizeIcon(nodes: IconNode): Promise<ToneMap> {
  const kebab = (k: string) => k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">` +
    nodes
      .map(
        ([tag, attrs]) =>
          `<${tag} ${Object.entries(attrs)
            .map(([k, v]) => `${kebab(k)}="${v}"`)
            .join(' ')}/>`,
      )
      .join('') +
    '</svg>'
  return rasterizeSrc('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg))
}

export function Services({ locale }: { locale: Locale }) {
  const t = useTranslations('home.services')
  const es = locale === 'es'
  const [active, setActive] = useState<number | null>(null)
  const [tones, setTones] = useState<Record<string, ToneMap>>({})
  const morphRef = useRef<HTMLDivElement>(null)
  const [fieldActive, setFieldActive] = useState(false)
  // Activate only on real scroll intent or when the section enters the
  // viewport — a generous rootMargin would fire during initial page load.
  useEffect(() => {
    const el = morphRef.current
    if (!el) return
    const activate = () => setFieldActive(true)
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        activate()
        io.disconnect()
      }
    })
    io.observe(el)
    const onScroll = () => {
      if (window.scrollY > 200) {
        activate()
        window.removeEventListener('scroll', onScroll)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      io.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [])
  useEffect(() => {
    if (!fieldActive || Object.keys(tones).length) return
    let dead = false
    const jobs: Promise<[string, ToneMap | null]>[] = [
      rasterizeIcon(ICON_NODES.launch).then((t) => ['launch', t] as [string, ToneMap]),
      rasterizeIcon(ICON_NODES.city).then((t) => ['city', t] as [string, ToneMap]),
      rasterizeIcon(ICON_NODES.network).then((t) => ['network', t] as [string, ToneMap]),
      rasterizeIcon(ICON_NODES.globe).then((t) => ['globe', t] as [string, ToneMap]),
      rasterizeSrc('/site/assets/circular-logo.svg')
        .then((t) => ['logo', t] as [string, ToneMap])
        .catch(() => ['logo', null] as unknown as [string, ToneMap]),
    ]
    Promise.all(jobs).then((entries) => {
      if (dead) return
      const map: Record<string, ToneMap> = {}
      for (const [k, v] of entries) if (v) map[k] = v
      setTones(map)
    })
    return () => {
      dead = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldActive])
  const cards = [
    {
      key: 'launch',
      title: t('newBusinessTitle'),
      desc: t('newBusinessDesc'),
      href: es ? '/es/servicios/nuevo-negocio' : '/services/new-business',
    },
    {
      key: 'city',
      title: t('enterpriseTitle'),
      desc: t('enterpriseDesc'),
      href: es ? '/es/servicios/empresa' : '/services/enterprise',
    },
    {
      key: 'network',
      title: t('aiTitle'),
      desc: t('aiDesc'),
      href: es ? '/es/servicios/ia' : '/services/ai',
    },
    {
      key: 'globe',
      title: t('latamTitle'),
      desc: t('latamDesc'),
      href: es ? '/es/servicios/latam' : '/services/latam',
    },
  ]
  const fieldTone =
    active === null ? (tones.logo ?? null) : (tones[cards[active].key] ?? tones.logo ?? null)
  return (
    <SiteSection
      id="what-we-do"
      className="home-services"
      label={t('label')}
      title={t('title')}
    >
      <div className="home-services-split" onPointerLeave={() => setActive(null)}>
        <div className="home-services-morph" ref={morphRef}>
          {fieldActive && (
            <Suspense fallback={null}>
              <ServiceDitherField tone={fieldTone} />
            </Suspense>
          )}
        </div>
        <div className="home-services-rows">
          {cards.map((card, i) => (
            <Link
              key={card.title}
              href={card.href}
              className={cn('home-service-card', 'home-service-card--row', active === i && 'active')}
              onPointerEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
            >
              <span className="home-service-num">{String(i + 1).padStart(2, '0')}</span>
              <div className="home-service-index-body">
                <h3 className="home-service-card-title">{card.title}</h3>
                <p className="home-service-card-desc">{card.desc}</p>
              </div>
              <ArrowUpRight className="home-service-arrow" size={22} strokeWidth={1.75} />
            </Link>
          ))}
        </div>
      </div>
    </SiteSection>
  )
}
